import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add, AddSquare, Box1 } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import {
  ConfirmDialog,
  FilterChip,
  PageHeader,
  SearchField,
  SefanoButton,
} from "@/components/Molecules";
import { ProductStatsRow, ProductsTable, SefanoPagination } from "@/components/Organisms";
import { PageTemplate } from "@/components/Templates/Page";
import type { ProductsTemplateProps } from "./Products.template.types";
import * as styles from "./Products.template.styles";

export type { ProductsTemplateProps } from "./Products.template.types";

export function ProductsTemplate({
  displayProducts,
  categories,
  categoryNames,
  meta,
  stats,
  page,
  setPage,
  search,
  handleSearchChange,
  activeCategoryId,
  setActiveCategoryId,
  productToDelete,
  isDeleting,
  handleDeleteRequest,
  handleDeleteConfirm,
  handleDeleteCancel,
  isLoading,
  isStatsLoading,
  isEmpty,
  paginationLabel,
  countLabel,
}: ProductsTemplateProps) {
  const { t } = useTranslation(["products", "common"]);
  const theme = useTheme();

  const filterChips = (
    <>
      <FilterChip
        label={t("products:filters.all")}
        active={activeCategoryId === "all"}
        onClick={() => setActiveCategoryId("all")}
      />
      {categories.map((category) => (
        <FilterChip
          key={category.id}
          label={category.name}
          active={activeCategoryId === category.id}
          onClick={() => setActiveCategoryId(category.id)}
        />
      ))}
    </>
  );

  const paginationSection =
    meta && meta.last_page > 1 ? (
      <Box sx={styles.paginationFooter}>
        <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
        <SefanoPagination count={meta.last_page} page={page} onChange={(_, value) => setPage(value)} />
      </Box>
    ) : meta ? (
      <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
    ) : null;

  return (
    <PageTemplate maxWidth={1440}>
      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopHeader}>
          <PageHeader
            title={t("products:title")}
            subtitle={t("products:subtitle")}
            action={
              <Link to="/products/create">
                  <SefanoButton
                    intent="add"
                    startIcon={<Add size={18} variant="Linear" color={theme.palette.primary.contrastText} />}
                  >
                    {t("products:createProduct")}
                  </SefanoButton>
                </Link>
            }
          />
        </Box>

        <Box sx={styles.mobileHeader}>
          <Box sx={styles.mobileHeaderText}>
            <Typography component="h1" sx={styles.mobileTitle}>
              {t("products:title")}
            </Typography>
            <Typography component="p" sx={styles.mobileCountLabel}>
              {countLabel}
            </Typography>
          </Box>
          {<Link to="/products/create">
              <SefanoButton
                intent="add"
                size="small"
                startIcon={<AddSquare size={18} variant="Linear" color={theme.palette.primary.contrastText} />}
                sx={styles.mobileCreateButton}
              >
                {t("products:mobileCreateProduct")}
              </SefanoButton>
            </Link>}
        </Box>

        {!isEmpty || isLoading ? (
          <>
            <ProductStatsRow
              stats={stats}
              categoriesCount={categories.length}
              isLoading={isStatsLoading}
            />

            <Box sx={styles.desktopContentCard}>
              <Box sx={styles.toolbar}>
                <Box sx={styles.toolbarFilters}>{filterChips}</Box>
                <Box sx={styles.desktopSearchWrap}>
                  <SearchField
                    placeholder={t("products:searchPlaceholder")}
                    value={search}
                    onChange={handleSearchChange}
                    fieldSize="compact"
                  />
                </Box>
              </Box>

              <ProductsTable
                products={displayProducts}
                categoryNames={categoryNames}
                isLoading={isLoading}
                onDelete={handleDeleteRequest}
                layout="desktop"
              />
              {paginationSection}
            </Box>

            <Box sx={styles.mobileStack}>
              <SearchField
                placeholder={t("products:searchPlaceholder")}
                value={search}
                onChange={handleSearchChange}
                fieldSize="compact"
              />

              <Box sx={styles.mobileFiltersRow}>{filterChips}</Box>

              <ProductsTable
                products={displayProducts}
                categoryNames={categoryNames}
                isLoading={isLoading}
                onDelete={handleDeleteRequest}
                layout="mobile"
              />
              {paginationSection}
            </Box>
          </>
        ) : (
          <>
            <Box sx={styles.desktopEmptyStateCard}>
              <Box sx={styles.emptyStateIconWrap}>
                <Box1 size={60} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {t("products:emptyTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={styles.emptyDescription}>
                {t("products:emptyDescription")}
              </Typography>
              {<Box sx={styles.emptyActionWrap}>
                  <Link to="/products/create">
                    <SefanoButton intent="add">{t("products:emptyAction")}</SefanoButton>
                  </Link>
                </Box>}
            </Box>

            <Box sx={styles.mobileEmptyState}>
              <Box sx={styles.mobileEmptyIconWrap}>
                <Box1 size={52} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography sx={styles.mobileEmptyTitle}>{t("products:emptyTitle")}</Typography>
              <Typography sx={styles.mobileEmptyDescription}>
                {t("products:mobileEmptyDescription")}
              </Typography>
              {<Link to="/products/create">
                  <SefanoButton
                    intent="add"
                    startIcon={<AddSquare size={19} variant="Linear" color={theme.palette.primary.contrastText} />}
                    sx={styles.mobileEmptyButton}
                  >
                    {t("products:emptyAction")}
                  </SefanoButton>
                </Link>}
            </Box>
          </>
        )}
      </Box>

      <ConfirmDialog
        open={productToDelete !== null}
        title={t("products:deleteConfirm.title")}
        message={t("products:deleteConfirm.message")}
        confirmLabel={t("common:actions.delete")}
        cancelLabel={t("common:actions.cancel")}
        isPending={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </PageTemplate>
  );
}
